import { createApi } from '@reduxjs/toolkit/query/react';
import API_ENDPOINTS from '../../apiEndpoints';
import { showWarningSnackbar } from '../../components/snackBar/snakbar';
import { axiosBaseQuery } from '../baseQuery';

export interface GstinAddress {
  State: string;
  BuildingNo: string;
  Street: string;
  District: string;
  landMark: string;
  Location: string;
  PinCode: string;
}

export interface GstinFetchResponse {
  TradeName: string;
  Status: string;
  LegalName: string;
  Address: GstinAddress;
  TaxPayerType: string;
  RegisteredDate: string;
  EinvoiceStatus: string;
  ConstitutionOfBusiness: string;
  Jurisdiction: string;
  AddressLine2: string;
  AddressLine1: string;
  GstIn: string;
  JurisdictionCode: string;
  AddressTest: string;
  LastUpdatedOn: string;
}

export interface GstinFetchError {
  code?: string;
  message: string;
  isNetwork: boolean;
  isTimeout: boolean;
}

type AxiosBaseQueryError = {
  status?: number | string;
  code?: string;
  data?: {
    error_cd?: string;
    message?: string;
  } | string;
};

/** Cache TTL: 10 minutes (same as keepUnusedDataFor). */
export const GSTIN_CACHE_TTL_SECONDS = 600;

/** Same value sent as `?gstin=` — also used as RTK cache key. */
export function normalizeGstinCacheKey(gstin: string): string {
  return (gstin || '').toString().trim().toUpperCase();
}

export const gstinFetchApi = createApi({
  reducerPath: 'gstinFetchApi',
  baseQuery: axiosBaseQuery,
  keepUnusedDataFor: GSTIN_CACHE_TTL_SECONDS,
  endpoints: (builder) => ({
    fetchGstinDetails: builder.query<GstinFetchResponse, string>({
      query: (gstin) => {
        const normalizedGstin = normalizeGstinCacheKey(gstin);
        return {
          url: API_ENDPOINTS.REACT_APP_FETCH_GSTIN_URL,
          method: 'GET',
          data: undefined,
          params: { gstin: normalizedGstin },
        };
      },
      /** Cache entry id = GSTIN sent to API (trimmed + uppercased). */
      serializeQueryArgs: ({ queryArgs }) => normalizeGstinCacheKey(queryArgs),
      transformResponse: (response: GstinFetchResponse) => response,
      transformErrorResponse: (response: AxiosBaseQueryError): GstinFetchError => {
        const errorCode =
          response?.data && typeof response.data === 'object' ? response.data.error_cd: undefined;
        const isTimeout = response?.code === 'ECONNABORTED' ||response?.status === 502 ||response?.status === 504;
        const isNetwork = response?.code === 'ERR_NETWORK' && !isTimeout;

        return {
          code: errorCode ?? response?.code,
          message: '',
          isNetwork,
          isTimeout,
        };
      },
      async onQueryStarted(_gstin, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const error = (err as { error?: GstinFetchError })?.error;
          if (!error) return;

          if (error.code === 'F08000' || error.code === 'SWEB_9035') {
            showWarningSnackbar('Invalid GSTIN as per GST Portal');
          } else if (error.isTimeout) {
            showWarningSnackbar(
              'GSTIN Portal is currently unavailable. Unable to validate or fetch details. Please enter details manually'
            );
          } else if (error.isNetwork) {
            showWarningSnackbar('Network error. Please check your internet connection');
          } else {
            showWarningSnackbar(
              'GSTIN Portal is currently unavailable. Unable to validate or fetch details. Please enter details manually'
            );
          }
        }
      },
      keepUnusedDataFor: GSTIN_CACHE_TTL_SECONDS,
    }),
  }),
});

export const { useFetchGstinDetailsQuery, useLazyFetchGstinDetailsQuery } = gstinFetchApi;

export default gstinFetchApi;
